/**
 * Cloudflare deployment URLs — single source of truth.
 *
 * Previously `PROD_API_URL = 'https://ubc-api.jhnosgoo.workers.dev'` was
 * copy-pasted into apps/public-portal/src/api-client.ts and
 * apps/operations-console/src/api-client.ts. Changing workers.dev subdomain
 * or worker name required finding both (easy to miss). This module fixes
 * that — one file, consumed by:
 *   - app api-clients (fallback when VITE_API_BASE_URL not set)
 *   - deploy script smoke URL (via `node -p`)
 *   - CORS allowed-origin generation (future: build wrangler.toml from here)
 *
 * Rules:
 *   - Keep zero runtime deps (no zod, no ajv). This module is imported by
 *     browser bundles — every byte matters.
 *   - One constant per thing. Don't add transformers here; consumers
 *     transform as they need.
 *   - Changes here are DEPLOYMENT changes — treat as wrangler.toml edits.
 */

// ── Workers (shared platform) ────────────────────────────────────────
export const API_WORKER_URL = 'https://ubc-api.jhnosgoo.workers.dev';
export const BFF_WORKER_URL = 'https://ubc-bff-ops.jhnosgoo.workers.dev';

// ── Legacy Pages projects (pre-venue-branding) ───────────────────────
export const LEGACY_OPS_URL = 'https://ubc-ops-console.pages.dev';
export const LEGACY_PORTAL_URL = 'https://ubc-public-portal.pages.dev';

// ── Venue-branded Pages (P2b) ────────────────────────────────────────
// Each entry { project, venueSlug, app } drives deploy script generation
// and CORS allowlist expansion. New venue = one new entry.
export interface VenuePagesEntry {
  /** CF Pages project name. Becomes `<project>.pages.dev` URL. */
  readonly project: string;
  /** Domain profile slug resolved by the backend (must match VenueProfile.slug). */
  readonly venueSlug: string;
  /** Which app bundle to deploy. */
  readonly app: 'public-portal' | 'operations-console';
}

export const VENUE_PAGES: readonly VenuePagesEntry[] = Object.freeze([
  // Legacy generic ops console (pre-venue-branding). Maps to idancing-arcade.
  { project: 'ubc-ops-console', venueSlug: 'idancing-arcade', app: 'operations-console' },
  // 店 1：愛跳跳方塊
  { project: 'idancing-book', venueSlug: 'idancing-arcade', app: 'public-portal' },
  { project: 'idancing-ops', venueSlug: 'idancing-arcade', app: 'operations-console' },
  // 店 2：烘焙教室 (baking-demo)
  { project: 'baking-book', venueSlug: 'baking-demo', app: 'public-portal' },
  { project: 'baking-ops', venueSlug: 'baking-demo', app: 'operations-console' },
]);

/**
 * All HTTPS origins that should be added to UBC_ALLOWED_ORIGINS. Generated
 * so adding a venue automatically updates CORS allowlist at deploy time.
 */
export function allProdOrigins(): readonly string[] {
  const pages = VENUE_PAGES.map((v) => `https://${v.project}.pages.dev`);
  return [LEGACY_OPS_URL, LEGACY_PORTAL_URL, ...pages];
}

/**
 * Lookup a VENUE_PAGES entry by its hostname (for the portal that derives
 * its venue slug from `window.location.host`).
 */
export function venueSlugForProject(project: string): string | null {
  const match = VENUE_PAGES.find((v) => v.project === project);
  return match?.venueSlug ?? null;
}

// ── Browser-side helpers (shared by portal + ops-console) ────────────

/**
 * Derive venue slug from `window.location.hostname` via VENUE_PAGES
 * registry, falling back to `<first-label without -book/-ops suffix>`.
 *
 *   idancing-book.pages.dev  → venueSlugForProject('idancing-book')  = 'idancing-arcade'
 *   baking-ops.pages.dev     → venueSlugForProject('baking-ops')     = 'baking-demo'
 *   localhost / 127.0.0.1    → devFallback or 'idancing-arcade'
 *
 * Shared so portal + ops-console don't each maintain a copy (DRY).
 */
export function venueSlugFromBrowserHost(opts?: {
  readonly hostname?: string;
  readonly devFallback?: string;
}): string {
  // `window` is Browser-only; keep this module dependency-free by
  // reading it through globalThis so tsc without "DOM" lib still types
  // clean. SSR / unit-test paths fall through to ''.
  const g = globalThis as unknown as {
    window?: { location?: { hostname?: string } };
  };
  const browserHost = g.window?.location?.hostname
    ? g.window.location.hostname.toLowerCase()
    : '';
  const hostname = (opts?.hostname ?? browserHost).toLowerCase();
  if (!hostname) return '';
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return opts?.devFallback ?? 'idancing-arcade';
  }
  const firstLabel = hostname.split('.')[0] ?? '';
  const registered = venueSlugForProject(firstLabel);
  if (registered) return registered;
  // Last-resort heuristic: strip `-book` / `-ops` / `-portal` / `-api`
  // suffix for unregistered projects. New deploys should always register
  // in VENUE_PAGES; this path exists only for forward-compat preview URLs.
  return firstLabel.replace(/-(?:book|ops|portal|api)$/, '');
}

/**
 * Attach `x-ubc-venue-slug` to a RequestInit so cross-origin API calls
 * (portal/ops → shared Worker) route to the correct venue. Safe to call
 * multiple times — won't overwrite an explicit header.
 */
export function withVenueHeader(
  init: RequestInit = {},
  slug?: string,
): RequestInit {
  const s = slug ?? venueSlugFromBrowserHost();
  if (!s) return init;
  const headers = new Headers(init.headers);
  if (!headers.has('x-ubc-venue-slug')) {
    headers.set('x-ubc-venue-slug', s);
  }
  return { ...init, headers };
}
